import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useSelector } from 'react-redux'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Check, ChevronLeft, ChevronRight, FileText, Film, ImagePlus, Mic, ShieldCheck, X } from 'lucide-react'
import { Spinner } from '@/components/ui/shadcn-io/spinner'
import { cn } from '@/lib/utils'
import type { RootState } from '@/lib/redux/store'
import { DOCUMENT_TYPE_LABELS, IMAGE_TYPES, MIN_IMAGES_PER_LISTING, VIDEO_TYPES, formatMYR, useAIIntake, type IntakeDraft, type IntakeFields, type IntakePricing, type HeldFile } from './aiIntake'
import { areaFields, areaReading, areaUnitFields, type AreaKey, type UnitChoice } from './area'
import { SECTIONS, isEmpty, problemWith, requiredLeft, rowsFor, toFillFor, type AgentContext, type Row } from './reviewFields'
import { AreaField, BoolField, ConfidenceBadge, FieldRow, SectionTitle, TextField } from './ReviewBits'
import { inputClass } from './reviewStyles'
import { useCriterias, usePropertySubTypes, type Choice } from './criteria'
import LocationFields from './LocationFields'
import LandFields from './LandFields'
import { MediaError, StageError, createListingFromDraft } from './createListing'
import { useAgentSettings } from '@/lib/agentSettings'

/**
 * Every listing the AI found, before any of them is created, in a dialog
 * opened from the chat: one listing at a time, up to ten.
 *
 * It is the listing form, filled in: every field is an input, carrying what
 * the AI read and empty where it read nothing, so a correction is one click
 * and a type. Only fields the form actually sends are here. A red ring marks
 * what the form will not submit without, and the button into the form waits
 * until none are left. Nothing here writes a listing: each one still goes
 * through the wizard and the existing approval workflow.
 */

/** Fields the form keeps as numbers; what is typed for them is read as one. */
const NUMERIC = new Set(['landArea', 'builtUp', 'noOfBedrooms', 'noOfBathrooms', 'noOfParkingSpots', 'noOfMaidrooms', 'yearOfCompletion', 'maintenanceFee', 'price'])

/** What was typed, as the draft stores it: a number where the form wants one, null when cleared. */
const parse = (key: string, raw: string): string | number | null => {
    const text = raw.trim()
    if (!text) return null
    if (!NUMERIC.has(key)) return text
    const n = Number(text.replace(/[,\s]/g, '').replace(/^RM/i, ''))
    return Number.isFinite(n) ? n : text
}

/** Counted in whole numbers: nobody has half a parking bay. */
const WHOLE = new Set(['noOfBedrooms', 'noOfBathrooms', 'noOfParkingSpots', 'noOfMaidrooms', 'yearOfCompletion', 'radius'])

const ENTERED = 'Entered by you.'

/** A photo is shown in the gallery, so it is left out of the list of documents. */
const IMAGE_NAME = /\.(jpe?g|png|webp|heic|gif)$/i

/** The form's ID for a picked value: the agent's, then the AI's, then by name. */
const idFor = (draft: IntakeDraft, row: Row, list: Choice[]): number | null => {
    const own = draft.fields[row.idKey ?? '']
    if (typeof own === 'number' && own > 0) return own
    const fromAI = (draft.category.ids as unknown as Record<string, number | null>)[row.idKey ?? '']
    if (typeof fromAI === 'number' && fromAI > 0) return fromAI
    const name = String(draft.fields[row.key] ?? '').trim().toLowerCase()
    return (name && list.find((c) => c.label.trim().toLowerCase() === name)?.value) || null
}

function ChoiceField({ row, draft, list, disabled, onPick }: { row: Row; draft: IntakeDraft; list: Choice[]; disabled: boolean; onPick: (row: Row, choice: Choice) => void }) {
    const id = idFor(draft, row, list)
    return (
        <Select
            disabled={disabled || !list.length}
            value={id ? String(id) : undefined}
            onValueChange={(v) => {
                const choice = list.find((c) => c.value === Number(v))
                if (choice) onPick(row, choice)
            }}>
            <SelectTrigger size='sm' className={cn('w-full', inputClass(!id, row.required), 'h-auto')}>
                <SelectValue placeholder={row.required ? 'Required' : 'Not provided'} />
            </SelectTrigger>
            <SelectContent>
                {list.map((c) => (
                    <SelectItem key={c.value} value={String(c.value)}>
                        {c.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )
}

function Section({
    title,
    rows,
    draft,
    agent,
    disabled,
    onField,
    onFields,
    units,
    lists,
    onPick,
}: {
    title: string
    rows: Row[]
    draft: IntakeDraft
    agent: AgentContext
    disabled: boolean
    onField: (key: string, raw: string | boolean | null) => void
    /** Several draft fields at once, already in the form the draft keeps them. */
    onFields: (fields: IntakeFields) => void
    /** The units the listing form offers for each area field. */
    units: Record<string, UnitChoice[]>
    lists: Record<string, Choice[]>
    onPick: (row: Row, choice: Choice) => void
}) {
    const visible = rowsFor(rows, draft, agent)
    if (!visible.length) return null
    return (
        <section>
            <SectionTitle>{title}</SectionTitle>
            <dl>
                {visible.map((r) => {
                    const v = draft.fields[r.key]
                    return (
                        <FieldRow key={r.key} label={r.label} required={r.required}>
                            {r.kind === 'bool' ? (
                                <BoolField value={typeof v === 'number' ? v === 1 : v === 'Yes' ? true : v === 'No' ? false : null} disabled={disabled} onChange={(next) => onField(r.key, next)} />
                            ) : r.kind === 'area' ? (
                                <AreaField
                                    reading={areaReading(draft.fields, r.key as AreaKey, units[r.key] ?? units.landArea)}
                                    units={units[r.key] ?? units.landArea}
                                    required={r.required}
                                    disabled={disabled}
                                    onChange={(value, unit) => onFields(areaFields(r.key as AreaKey, value, unit))}
                                    onUnit={(unit) => onFields(areaUnitFields(r.key as AreaKey, draft.fields, unit))}
                                />
                            ) : r.choices ? (
                                <ChoiceField row={r} draft={draft} list={lists[r.choices] ?? []} disabled={disabled} onPick={onPick} />
                            ) : (
                                <TextField
                                    value={isEmpty(v) ? '' : String(v)}
                                    required={r.required}
                                    numeric={r.kind === 'number'}
                                    whole={WHOLE.has(r.key)}
                                    money={r.key === 'maintenanceFee'}
                                    suffix={r.suffix}
                                    problem={problemWith(draft, r)}
                                    disabled={disabled}
                                    onCommit={(raw) => onField(r.key, raw)}
                                />
                            )}
                        </FieldRow>
                    )
                })}
            </dl>
        </section>
    )
}

function PriceRow({ pricing, disabled, onPrice }: { pricing: IntakePricing; disabled: boolean; onPrice: (raw: string) => void }) {
    const known = pricing.status !== 'unknown' && pricing.price !== null
    const estimated = pricing.status === 'estimated'
    return (
        <section>
            <SectionTitle aside={estimated && <ConfidenceBadge level={pricing.confidence} title={pricing.basis} />}>Price</SectionTitle>
            <dl>
                <FieldRow label='Asking price' required>
                    <div className='space-y-1'>
                        <div className='flex items-center gap-2'>
                            <TextField value={known ? String(pricing.price) : ''} required numeric money suffix='RM' disabled={disabled} onCommit={onPrice} />
                            {estimated && !disabled && (
                                <Button type='button' size='sm' variant='outline' className='h-8 shrink-0 px-2 text-xs' onClick={() => onPrice(String(pricing.price))}>
                                    <Check className='h-3 w-3' /> Confirm
                                </Button>
                            )}
                        </div>
                        {estimated && (
                            <p className='text-xs text-muted-foreground'>
                                AI estimate{pricing.low !== null && pricing.high !== null && `, ${formatMYR(pricing.low)} – ${formatMYR(pricing.high)}`}
                                {pricing.perUnitRate !== null && pricing.perUnitLabel && ` · about RM${pricing.perUnitRate.toLocaleString('en-MY')} per ${pricing.perUnitLabel}`}. Confirm or change it.
                            </p>
                        )}
                        {!known && <p className='text-xs text-muted-foreground'>Not in your files, and not enough to estimate from.</p>}
                    </div>
                </FieldRow>
            </dl>
        </section>
    )
}

/** One listing, laid out like the form it feeds. */
function DraftCard({ index, busy, agent }: { index: number; busy: boolean; agent: AgentContext }) {
    const { drafts, files, assignments, completed, addFiles, removeFile, toggleAssignment, imageCountFor, updateDraft } = useAIIntake()
    const photoInput = useRef<HTMLInputElement>(null)
    const criterias = useCriterias()
    const draft = drafts[index]
    const propertyTypeId = idFor(draft, { key: 'propertyType', label: '', kind: 'select', idKey: 'propertyTypeId' }, criterias.propertyTypes)
    const propertySubTypes = usePropertySubTypes(propertyTypeId)
    // Only the numbered lists go to the generic rows; the land title's code
    // list is a different shape and is used by the land section.
    // The dropdown lists; the unit lists are a different shape and go to the area fields.
    const { landAreaUnits, builtUpUnits, ...choiceLists } = criterias
    const lists: Record<string, Choice[]> = { ...choiceLists, propertySubTypes }
    // The photos and videos in this listing; removing one takes it out.
    const media = (assignments[index] ?? []).map((id) => files.find((f) => f.id === id)).filter((f): f is HeldFile => Boolean(f))
    const images = imageCountFor(index)
    const short = Math.max(0, MIN_IMAGES_PER_LISTING - images)
    const isDone = completed.includes(index)
    const locked = busy || isDone
    const category = [draft.category.listingType, draft.category.propertyType, draft.category.propertySubType].filter((x, idx, all) => x && all.indexOf(x) === idx)

    const setField = (key: string, raw: string | boolean | null) =>
        updateDraft(index, (d) => ({
            ...d,
            fields: { ...d.fields, [key]: typeof raw === 'boolean' ? (raw ? 'Yes' : 'No') : raw === null ? null : parse(key, raw) },
            missingFields: d.missingFields.filter((k) => k !== key),
        }))

    const setFields = (fields: IntakeFields) =>
        updateDraft(index, (d) => ({
            ...d,
            fields: { ...d.fields, ...fields },
            missingFields: d.missingFields.filter((k) => !Object.hasOwn(fields, k)),
        }))

    /** A pick from one of the form's lists: its name for reading, its ID for the form. */
    const pick = (row: Row, choice: Choice) =>
        updateDraft(index, (d) => ({
            ...d,
            fields: {
                ...d.fields,
                [row.key]: choice.label,
                ...(row.idKey ? { [row.idKey]: choice.value } : {}),
                // The sub-types belong to the type, so an old one cannot stay.
                ...(row.key === 'propertyType' && choice.value !== propertyTypeId ? { propertySubType: null, propertySubTypeId: null } : {}),
            },
            missingFields: d.missingFields.filter((k) => k !== row.key),
        }))

    // A price the agent types or confirms is the asking price: stated, not estimated.
    const setPrice = (raw: string) => {
        const price = parse('price', raw)
        if (typeof price !== 'number') return
        updateDraft(index, (d) => ({
            ...d,
            fields: { ...d.fields, price },
            missingFields: d.missingFields.filter((k) => k !== 'price'),
            pricing: { ...d.pricing, status: 'stated', price, low: null, high: null, basis: ENTERED, assumptions: [] },
        }))
    }

    return (
        <div className={cn('space-y-6', isDone && 'opacity-60')}>
            {/* Photos first: the listing cannot be created without them. */}
            <section>
                <SectionTitle aside={<span className={cn('text-xs', short ? 'text-red-600' : 'text-muted-foreground')}>{images} of {MIN_IMAGES_PER_LISTING} photos</span>}>
                    Photos<span className='ml-0.5 text-red-600'>*</span>
                </SectionTitle>
                <div className='flex flex-wrap gap-2'>
                    {media.map((f) => (
                        <div key={f.id} title={f.file.name} className='group relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-gray-200'>
                            {f.preview ? (
                                <img src={f.preview} alt={f.file.name} className='h-full w-full object-cover' />
                            ) : (
                                <span className='flex h-full w-full items-center justify-center bg-gray-100'>
                                    <Film className='h-4 w-4 text-gray-500' />
                                </span>
                            )}
                            {!locked && (
                                <button
                                    type='button'
                                    aria-label={`Remove ${f.file.name}`}
                                    onClick={() => (drafts.length === 1 ? removeFile(f.id) : toggleAssignment(index, f.id))}
                                    className='absolute right-1 top-1 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-black/70 text-white transition-colors hover:bg-black'>
                                    <X className='h-3 w-3' />
                                </button>
                            )}
                        </div>
                    ))}
                    {!locked && (
                        <button
                            type='button'
                            onClick={() => photoInput.current?.click()}
                            className={cn(
                                'flex h-20 w-20 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-[11px] transition-colors hover:border-gray-500 hover:text-black',
                                short ? 'border-red-300 text-red-600' : 'border-gray-300 text-muted-foreground',
                            )}>
                            <ImagePlus className='h-4 w-4' />
                            Add
                        </button>
                    )}
                </div>
                <p className='mt-2 text-xs text-muted-foreground'>
                    {short > 0 && !isDone ? `Add ${short} more photo${short === 1 ? '' : 's'}.` : ''}
                </p>
                <input
                    ref={photoInput}
                    type='file'
                    multiple
                    accept={[...IMAGE_TYPES, ...VIDEO_TYPES].join(',')}
                    className='hidden'
                    onChange={(e) => {
                        addFiles(Array.from(e.target.files || []), index)
                        e.target.value = ''
                    }}
                />
            </section>

            <section>
                <SectionTitle aside={category.length > 0 && <ConfidenceBadge level={draft.category.confidence} title={draft.category.reason} />}>Headline</SectionTitle>
                <TextField value={draft.fields.headline ? String(draft.fields.headline) : ''} required disabled={locked} onCommit={(raw) => setField('headline', raw)} />
            </section>

            <section>
                <SectionTitle>Description for buyers</SectionTitle>
                <TextField
                    value={draft.fields.description ? String(draft.fields.description) : ''}
                    required
                    multiline
                    disabled={locked}
                    onCommit={(raw) => setField('description', raw)}
                />
            </section>

            <PriceRow pricing={draft.pricing} disabled={locked} onPrice={setPrice} />

            {SECTIONS.map((s) =>
                s.title === 'Location' ? (
                    <LocationFields key={s.title} index={index} disabled={locked} rows={s.rows} />
                ) : s.title === 'Land details' ? (
                    <LandFields key={s.title} index={index} disabled={locked} rows={s.rows} />
                ) : (
                    <Section key={s.title} title={s.title} rows={s.rows} draft={draft} agent={agent} disabled={locked} onField={setField} onFields={setFields} units={{ landArea: landAreaUnits, builtUp: builtUpUnits }} lists={lists} onPick={pick} />
                ),
            )}

        </div>
    )
}

interface AIListingReviewProps {
    /** The listing shown, or null when the dialog is closed. */
    index: number | null
    onIndexChange: (index: number) => void
    onClose: () => void
    busy: boolean
    onOpen: (draft: IntakeDraft, draftIndex: number) => void
}

export default function AIListingReview({ index, onIndexChange, onClose, busy, onOpen }: AIListingReviewProps) {
    const { drafts, sources, completed, created, proofs, session, imageCountFor, mediaFor, markCompleted } = useAIIntake()
    const navigate = useNavigate()
    const { settings } = useAgentSettings()
    const [creating, setCreating] = useState(false)
    const isInternal = useSelector((state: RootState) => Boolean(state.auth.agent?.isInternal))
    const agent: AgentContext = { isInternal }
    const count = drafts.length
    const current = index !== null && index < count ? index : null

    // Arrow keys page through the listings, unless the agent is typing.
    useEffect(() => {
        if (current === null || count < 2) return
        const onKey = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null
            if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return
            if (e.key === 'ArrowRight' && current < count - 1) onIndexChange(current + 1)
            if (e.key === 'ArrowLeft' && current > 0) onIndexChange(current - 1)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [current, count, onIndexChange])

    /**
     * Creates the listing from what is on this screen. It only runs once
     * nothing required is left, so the form has nothing more to ask.
     */
    const generate = async () => {
        if (current === null || creating || completed.includes(current)) return
        setCreating(true)
        try {
            const listingId = await createListingFromDraft({
                draft: drafts[current],
                draftIndex: current,
                media: mediaFor(current),
                proof: proofs[current] ?? null,
                sessionPublicId: session?.publicId,
                onGenerate: settings.listingOnGenerate,
                requireOwnerValidation: Boolean(settings.requireOwnerValidation),
            })
            markCompleted(current, listingId)
            toast.success('Listing generated.', {
                description:
                    settings.listingOnGenerate === 'publish'
                        ? 'It is live on the site now.'
                        : settings.listingOnGenerate === 'submit'
                          ? 'It has gone for approval.'
                          : 'It is in your drafts, ready to submit for approval.',
                action: { label: 'View', onClick: () => navigate(`/listings/edit/${listingId}`) },
                duration: 10000,
            })
            onClose()
        } catch (err: unknown) {
            if (err instanceof MediaError) {
                markCompleted(current, err.listingId)
                toast.error(`Listing generated, but its photos did not upload: ${err.message}`, {
                    action: { label: 'Add them', onClick: () => navigate(`/listings/edit/${err.listingId}`) },
                    duration: 10000,
                })
                onClose()
                return
            }
            if (err instanceof StageError) {
                markCompleted(current, err.listingId)
                toast.error(`Listing generated, but it stayed in your drafts: ${err.message}`, {
                    action: { label: 'Open it', onClick: () => navigate(`/listings/edit/${err.listingId}`) },
                    duration: 10000,
                })
                onClose()
                return
            }
            const e = err as { response?: { data?: { error?: string } }; message?: string }
            toast.error(e.response?.data?.error ?? e.message ?? 'The listing could not be generated.')
        } finally {
            setCreating(false)
        }
    }

    if (current === null) return <Dialog open={false} />
    const draft = drafts[current]
    const isDone = completed.includes(current)
    const made = created.find((x) => x.draftIndex === current)
    const left = requiredLeft(draft, imageCountFor(current), agent, Boolean(proofs[current]))
    const heard = sources.filter((s) => s.transcript)

    return (
        <Dialog open onOpenChange={(next) => !next && onClose()}>
            <DialogContent
                // Google's suggestions sit outside the dialog; picking one is not a click away.
                onInteractOutside={(e) => {
                    if ((e.target as HTMLElement | null)?.closest?.('.pac-container')) e.preventDefault()
                }}
                className='flex h-[92dvh] w-[calc(100%-1rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl'>
                <div className='border-b px-6 pb-3 pr-12 pt-5'>
                    <DialogTitle className='text-base'>{count === 1 ? 'Review listing' : `Listing ${current + 1} of ${count}`}</DialogTitle>
                    <DialogDescription className='sr-only'>Check the listing the AI drafted and fill in what it could not.</DialogDescription>

                    {count > 1 && (
                        <div className='mt-3 flex items-center gap-1.5'>
                            <Button type='button' size='icon' variant='ghost' className='h-8 w-8 shrink-0' disabled={current === 0} onClick={() => onIndexChange(current - 1)} aria-label='Previous listing'>
                                <ChevronLeft className='h-4 w-4' />
                            </Button>
                            <div className='flex min-w-0 flex-1 gap-1.5 overflow-x-auto py-0.5'>
                                {drafts.map((d, i) => {
                                    const done = completed.includes(i)
                                    const open = toFillFor(d, imageCountFor(i), agent, Boolean(proofs[i]))
                                    return (
                                        <button
                                            key={i}
                                            type='button'
                                            onClick={() => onIndexChange(i)}
                                            title={`${String(d.fields.headline || `Listing ${i + 1}`)}${done ? ' · created' : open ? ` · ${open} required` : ' · ready'}`}
                                            className={cn(
                                                'flex h-8 min-w-8 shrink-0 cursor-pointer items-center justify-center gap-1 rounded-full border px-2.5 text-xs font-medium transition-colors',
                                                i === current ? 'border-black bg-black text-white' : 'bg-white text-gray-700 hover:border-gray-400',
                                            )}>
                                            {done ? <Check className='h-3 w-3' /> : i + 1}
                                            {!done && open > 0 && <span className='h-1.5 w-1.5 rounded-full bg-red-500' />}
                                        </button>
                                    )
                                })}
                            </div>
                            <Button type='button' size='icon' variant='ghost' className='h-8 w-8 shrink-0' disabled={current === count - 1} onClick={() => onIndexChange(current + 1)} aria-label='Next listing'>
                                <ChevronRight className='h-4 w-4' />
                            </Button>
                        </div>
                    )}
                </div>

                <div className='min-h-0 flex-1 overflow-y-auto px-6 py-6'>
                    {sources.length > 0 && (
                        <ul className='mb-5 flex flex-wrap gap-1.5'>
                            {sources.filter((s) => !IMAGE_NAME.test(s.file)).map((s) => (
                                <li key={`${s.file}-${s.documentType}`} className='flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700' title={s.proofOfOwnership && s.proofReason ? `${s.description} ${s.proofReason}` : s.description}>
                                    {s.documentType === 'voice_note' ? <Mic className='h-3 w-3' /> : s.proofOfOwnership ? <ShieldCheck className='h-3 w-3 text-emerald-600' /> : <FileText className='h-3 w-3' />}
                                    <span className='max-w-[180px] truncate'>{s.file}</span>
                                    <span className='text-muted-foreground'>· {DOCUMENT_TYPE_LABELS[s.documentType] ?? 'Document'}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {/* What the AI heard in an attached recording, so a misheard
                        number is caught before it becomes the price. */}
                    {heard.map((s) => (
                        <details key={`heard-${s.file}`} className='mb-5 rounded-lg border p-3'>
                            <summary className='flex cursor-pointer items-center gap-1.5 text-xs font-medium text-gray-700'>
                                <Mic className='h-3.5 w-3.5' /> What I heard in {s.file}
                            </summary>
                            <p className='mt-2 whitespace-pre-line text-sm text-gray-700'>{s.transcript}</p>
                        </details>
                    ))}
                    <DraftCard key={current} index={current} busy={busy} agent={agent} />
                </div>

                {/* The way into the form, and what is still stopping it. */}
                <div className='flex flex-wrap items-center justify-between gap-3 border-t px-6 py-3'>
                    <div className='text-xs'>
                        {isDone ? (
                            made ? (
                                <Link to={`/listings/edit/${made.listingId}`} className='inline-flex items-center gap-1 font-medium underline-offset-4 hover:underline'>
                                    <Check className='h-3 w-3' /> Created · open it
                                </Link>
                            ) : (
                                <span className='inline-flex items-center gap-1'>
                                    <Check className='h-3 w-3' /> Created
                                </span>
                            )
                        ) : left.length > 0 ? (
                            <span className='text-red-600'>
                                Still needed: {left.slice(0, 3).join(', ')}
                                {left.length > 3 && ` and ${left.length - 3} more`}
                            </span>
                        ) : (
                            <span className='inline-flex items-center gap-1 text-green-700'>
                                <Check className='h-3 w-3' /> Everything the form needs is here
                            </span>
                        )}
                    </div>
                    <div className='flex items-center gap-2'>
                        {/* The long way round, for anything the review does not carry. */}
                        {!isDone && (
                            <Button variant='outline' disabled={busy || creating} onClick={() => onOpen(draft, current)}>
                                Open full form
                            </Button>
                        )}
                        <Button disabled={busy || isDone || creating || left.length > 0} onClick={generate}>
                            {creating ? (
                                <>
                                    <Spinner variant='circle' className='h-4 w-4' /> Generating…
                                </>
                            ) : isDone ? (
                                'Already created'
                            ) : (
                                'Generate listing'
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
